import { StateCreator } from "zustand";

/**
 * Notifications slice state.
 *
 * NOTE: This slice only holds client-only UI state (pagination).
 * Server data (notifications, unreadCount, hasMore) lives in React Query.
 * See architecture.mdc for the "React Query as Source of Truth" pattern.
 */
export type NotificationsSliceState = {
  /** Current page for pagination */
  notificationsPage: number;
};

/**
 * Notifications slice actions.
 */
export type NotificationsSliceActions = {
  setNotificationsPage: (page: number) => void;
  resetNotificationsPage: () => void;
};

/**
 * Full notifications slice type.
 */
export type NotificationsSlice = NotificationsSliceState &
  NotificationsSliceActions;

/**
 * Initial state for notifications slice.
 */
export const initialNotificationsState: NotificationsSliceState = {
  notificationsPage: 1,
};

/**
 * Creates the notifications slice for Zustand store.
 * Only manages pagination state - notification data comes from React Query.
 *
 * @example
 * ```typescript
 * const useStore = create<NotificationsSlice>()((...a) => ({
 *   ...createNotificationsSlice(...a),
 * }));
 *
 * // In a component:
 * const page = useStore((s) => s.notificationsPage);
 * const setPage = useStore((s) => s.setNotificationsPage);
 * const { data, isLoading } = useFetchNotifications({ user, page });
 * ```
 */
export const createNotificationsSlice: StateCreator<
  NotificationsSlice,
  [],
  [],
  NotificationsSlice
> = (set) => ({
  ...initialNotificationsState,

  setNotificationsPage: (notificationsPage) => set({ notificationsPage }),

  resetNotificationsPage: () => set(initialNotificationsState),
});

// --- Selectors ---
export const notifications = {
  pagination: (s: NotificationsSlice) => ({
    page: s.notificationsPage,
    setPage: s.setNotificationsPage,
    reset: s.resetNotificationsPage,
  }),
};
